import { type ComponentType } from 'react';
import { FeatureItem, type FeatureItemProps } from './FeatureItem';
import { FeatureDetailRows, PropertyRow } from './Layer';
import { getLatLongFromGeometry } from './utils/getLatLongFromGeometry';
import {
  readLocationCustomFields,
  type StoredField,
} from './utils/locationPopup';

type InternalListFeature = Required<
  NonNullable<FeatureItemProps['internalFeature']>
> & {
  fields?: StoredField[];
};

type InternalFeatureListProps = {
  features: InternalListFeature[];
  /** Preferred: zoom + open ArcGIS feature popup. */
  onFocusFeature?: (feature: InternalListFeature) => Promise<void> | void;
  /** Fallback for callers that only pan (e.g. pending add-layer drafts). */
  onZoomToLocation?: (params: {
    longitude: number;
    latitude: number;
    zoom?: number;
  }) => Promise<void> | undefined | void;
  onEdit?: (featureId: string) => Promise<void> | void;
  onDelete?: (featureId: string) => Promise<void> | void;
  editingFeatureId?: string | null;
  /**
   * Override FeatureItem chrome (e.g. SitStat shadcn). Defaults to LayersTab
   * FeatureItem.
   */
  FeatureItemComponent?: ComponentType<FeatureItemProps>;
};

export function InternalFeatureList({
  features,
  onFocusFeature,
  onZoomToLocation,
  onEdit,
  onDelete,
  editingFeatureId,
  FeatureItemComponent = FeatureItem,
}: InternalFeatureListProps) {
  const Item = FeatureItemComponent;

  return (
    <ul className="tw:!m-0 tw:!flex tw:!w-full tw:!list-none tw:!flex-col tw:!gap-1 tw:!overflow-y-auto tw:!p-0 tw:max-h-52">
      {features
        .filter((feature) => feature.featureId !== editingFeatureId)
        .map((feature) => {
          let coords: { longitude: number; latitude: number } | null = null;
          try {
            coords = getLatLongFromGeometry(feature.geometry);
          } catch {
            coords = null;
          }

          return (
            <Item
              key={feature.key}
              internalFeature={feature}
              title={feature.title}
              onZoom={() => {
                if (onFocusFeature) {
                  return onFocusFeature(feature);
                }
                if (!coords) return;
                return onZoomToLocation?.({ ...coords });
              }}
              onEdit={onEdit}
              onDelete={onDelete}
              isEditing={editingFeatureId === feature.featureId}
            >
              {feature.fields && feature.fields.length > 0 ? (
                feature.fields.map((field, index) => (
                  <PropertyRow
                    key={`${field.label}-${index}`}
                    label={field.label}
                    value={field.value}
                  />
                ))
              ) : (
                <FeatureDetailRows
                  lastUpdated={feature.updatedAt}
                  point={coords ?? undefined}
                  customFields={readLocationCustomFields(feature.properties)}
                />
              )}
            </Item>
          );
        })}
    </ul>
  );
}
